/**
 * Bounded async call with optional retry — wraps a callable so a hung/cold
 * gen2 instance can't leave a spinner up to the 70s client default.
 *
 * IMPORTANT: retry-on-timeout is only safe for IDEMPOTENT reads. A timed-out
 * mutation (e.g. answerQuestion) may have succeeded server-side, so retrying it
 * risks a double-submit — pass `retries: 0` for those.
 */
export class CallTimeoutError extends Error {
  constructor(timeoutMs: number) {
    super(`Call timed out after ${timeoutMs}ms`);
    this.name = 'CallTimeoutError';
  }
}

export interface CallWithTimeoutOptions {
  /** Milliseconds before a single attempt is considered failed. Default 15000. */
  timeoutMs?: number;
  /** Extra attempts after a TIMEOUT only. Default 1. Use 0 for mutations. */
  retries?: number;
}

export async function callWithTimeout<T>(
  factory: () => Promise<T>,
  { timeoutMs = 15000, retries = 1 }: CallWithTimeoutOptions = {}
): Promise<T> {
  let lastError: unknown;

  for (let attempt = 0; attempt <= retries; attempt++) {
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      return await Promise.race([
        factory(),
        new Promise<never>((_, reject) => {
          timer = setTimeout(() => reject(new CallTimeoutError(timeoutMs)), timeoutMs);
        }),
      ]);
    } catch (error) {
      lastError = error;
      // Only a timeout is retryable here. Real backend errors (permission,
      // not-found, validation) must surface immediately — retrying won't help
      // and would mask the cause.
      if (!(error instanceof CallTimeoutError)) throw error;
    } finally {
      if (timer) clearTimeout(timer);
    }
  }

  throw lastError;
}
